import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import Section from '@components/Section';
import Contact, {
  networkStrengthIconForBucket,
  networkStrengthLabelForBucket,
} from '@models/Contact';
import { useWorkspaceContext } from '@support/hooks/use-workspace-context';
import Avatar from '@components/Avatar';
import styles from './NetworkStrength.module.scss';
import Workspace, { WorkspaceMember } from '@models/Workspace';

export const NetworkStrengthPill = ({
  bucket,
  teamMember,
}: {
  bucket: number;
  teamMember?: WorkspaceMember;
}) => {
  const Icon = networkStrengthIconForBucket(bucket);
  const label = networkStrengthLabelForBucket(bucket);

  if (!teamMember) {
    return (
      <span className={cn(styles.badge, styles.singlePlayer)}>
        <Icon />
        {label}
      </span>
    );
  }

  return (
    <span className={styles.badge}>
      <Avatar
        url={teamMember!.avatarURL}
        initials={teamMember!.firstName[0]}
        size="xs"
        name={teamMember!.displayName}
      />
      <Icon />
    </span>
  );
};

export const contactNetworkStrengthsForWorkspace = (contact: Contact, workspace: Workspace) => {
  return (contact?.networkStrengthWithUserTeamMembers || [])
    .map((networkStrength) => {
      const teamMember = workspace?.members?.get(String(networkStrength.userID));
      return teamMember ? { teamMember, bucket: networkStrength.bucket } : null;
    })
    .filter((networkStrength) => !!networkStrength);
};

const initialNumberOfMembersShown = 3;

const MultiPlayerNetworkStrengths = ({ contact }: { contact: Contact }) => {
  const { activeWorkspace } = useWorkspaceContext();
  const [allShown, setAllShown] = useState(
    activeWorkspace.members.size <= initialNumberOfMembersShown,
  );
  const [btnClicked, setBtnClicked] = useState(false);
  const teamMemberNetworkStrengths = useMemo(
    () => contactNetworkStrengthsForWorkspace(contact, activeWorkspace),
    [contact?.id, contact?.networkStrengthWithUserTeamMembers?.length],
  );

  useEffect(() => {
    if (allShown && teamMemberNetworkStrengths?.length > initialNumberOfMembersShown) {
      setAllShown(false);
    }
  }, [teamMemberNetworkStrengths]);

  return (
    teamMemberNetworkStrengths?.length > 0 && (
      <Section>
        <Section.Title underline>Network Strength</Section.Title>
        <div className={styles.items}>
          {teamMemberNetworkStrengths
            .slice(
              0,
              allShown && btnClicked
                ? teamMemberNetworkStrengths.length
                : initialNumberOfMembersShown,
            )
            .map((networkStrength, idx) => {
              const { teamMember, bucket } = networkStrength!;
              return (
                <div
                  className={cn(
                    styles.item,
                    idx > initialNumberOfMembersShown - 1 && styles.fadeIn,
                  )}
                  key={teamMember.id}
                >
                  <NetworkStrengthPill bucket={bucket} teamMember={teamMember} />
                  {teamMember!.displayName}
                </div>
              );
            })}
        </div>
        {!allShown && teamMemberNetworkStrengths?.length > initialNumberOfMembersShown && (
          <button
            onClick={() => {
              !btnClicked && setBtnClicked(true);
              setAllShown(true);
            }}
            className={cn('btn-none', styles.showAllBtn)}
          >
            View {teamMemberNetworkStrengths.length - initialNumberOfMembersShown} more
          </button>
        )}
      </Section>
    )
  );
};

const SinglePlayerNetworkStrengths = ({ contact }: { contact: Contact }) => {
  if ((contact?.networkStrengthWithUserTeamMembers?.length || 0) === 0) {
    return null;
  }

  const bucket = contact.networkStrengthWithUserTeamMembers[0].bucket;

  return (
    <Section>
      <Section.Title underline>Network Strength</Section.Title>
      <div className={styles.items}>
        <NetworkStrengthPill bucket={bucket} />
      </div>
    </Section>
  );
};

export default function NetworkStrength({ contact }: { contact: Contact; underline?: boolean }) {
  const { isMultiPlayer } = useWorkspaceContext();

  if (!isMultiPlayer) {
    return <SinglePlayerNetworkStrengths contact={contact} />;
  }

  return <MultiPlayerNetworkStrengths contact={contact} />;
}
